import React, { useEffect, useState, useContext } from 'react';
import {
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Button,
  Toolbar,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TableSortLabel,
  TextField,
  Box,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';
import AddUserModal from './AddUserModal';
import EditUserModal from './EditUserModal';
import { User, Region } from '../types';
import { AppContext } from '../../AppContext';
import { useAuth0 } from '@auth0/auth0-react';
import { getComparator, Order } from '../utils';
import { canManageRole, getRoleLabel } from '../roles';
import Snackbar from '@mui/material/Snackbar';
import CircularProgress from '@mui/material/CircularProgress';

type AlertSeverity = 'error' | 'success' | 'info' | 'warning' | undefined;

const UsersPage: React.FC = () => {
  const { user: auth0User } = useAuth0();
  const { user: currentUser } = useContext(AppContext);

  const [users, setUsers] = useState<User[]>([]);
  const [regions, setRegions] = useState<Region[]>([]);
  const [addingUser, setAddingUser] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [alertSeverity, setAlertSeverity] = useState<AlertSeverity>('info');
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [openAddUserModal, setOpenAddUserModal] = useState(false);
  const [openEditUserModal, setOpenEditUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [deleteUserId, setDeleteUserId] = useState<string | null>(null);
  const [deleteUserSub, setDeleteUserSub] = useState<string | undefined>(undefined);
  const [deleteUserEmail, setDeleteUserEmail] = useState<string>('');
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<keyof User>('email');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  const getUsers = async (region_id: any) => {
    const response = await axios.get(`${process.env.REACT_APP_API_URL}/users`, {
      params: { region_id },
    });
    return response;
  };

  useEffect(() => {
    const fetchAll = async () => {
      try {
        setLoading(true);
        const regionId = currentUser?.region_id;

        const [usersResponse, regionsResponse] = await Promise.all([
          getUsers(regionId),
          axios.get(`${process.env.REACT_APP_API_URL}/regions`),
        ]);

        setUsers(usersResponse.data);
        setRegions(regionsResponse.data);
      } catch (error) {
        console.error('Failed to fetch users/regions', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, [currentUser?.region_id]);

  const handleAddUser = async (
    email: string,
    password: string,
    role_id: number,
    region_id: string
  ) => {
    setAddingUser(true);
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/add-user`, {
        email,
        password,
        role_id,
        region_id,
        auth0_user_sub: auth0User?.sub,
      });

      const response = await axios.get(`${process.env.REACT_APP_API_URL}/users`);
      setOpenAddUserModal(false);
      setUsers(response.data);
      setAlertSeverity('success');
      const msg = 'Successfully created user';
      setSnackbarMessage(msg);
    } catch (error: any) {
      setAlertSeverity('error');
      const msg = 'Failed to add user. Please try again later.';
      setSnackbarMessage(msg);
    } finally {
      setSnackbarOpen(true);
      setAddingUser(false);
    }
  };

  const handleEditClick = (user: User) => {
    setEditingUser(user);
    setOpenEditUserModal(true);
  };

  const handleUpdateUser = async () => {
    getUsers(currentUser?.region_id ?? 'All').then((response) => {
      setUsers(response.data);
    });
    setOpenEditUserModal(false);
    setEditingUser(null);
  };

  const handleDeleteClick = (user: User) => {
    setDeleteUserId(user.id);
    setDeleteUserSub(user.auth0_sub);
    setDeleteUserEmail(user.email);
  };

  const handleConfirmDelete = async () => {
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/delete-user`, {
        data: {
          user_id: deleteUserId,
          auth0_sub: deleteUserSub,
          current_auth0_sub: auth0User?.sub,
        },
      });

      const response = await axios.get(`${process.env.REACT_APP_API_URL}/users`);
      setUsers(response.data);
      setDeleteUserId(null);
      setDeleteUserEmail('');
    } catch (error) {
      console.error('Failed to delete user', error);
    }
  };

  const handleCancelDelete = () => {
    setDeleteUserId(null);
    setDeleteUserEmail('');
  };

  const handleRequestSort = (property: keyof User) => {
    const isAscending = orderBy === property && order === 'asc';
    setOrder(isAscending ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const filteredUsers = users.filter((user) => {
    if (!searchTerm.trim()) return true;

    const term = searchTerm.toLowerCase();
    const email = user.email.toLowerCase();
    const regionName = (user.region || '').toLowerCase();
    const roleLabel = getRoleLabel(user.role_id);

    return (
      email.includes(term) ||
      regionName.includes(term) ||
      roleLabel.includes(term)
    );
  });

  const sortedUsers = filteredUsers.slice().sort(getComparator(order, orderBy));

  return (
    <Container maxWidth="xl">
      <Toolbar />

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Button
          variant="contained"
          color="primary"
          onClick={() => setOpenAddUserModal(true)}
        >
          Add User
        </Button>

        <TextField
          size="small"
          label="Search"
          variant="outlined"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search email, role, or region..."
          sx={{ minWidth: 260 }}
        />
      </Box>

      {loading && users.length === 0 ? (
        <Box
          sx={{
            height: 300,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 1,
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer
          component={Paper}
          style={{ maxHeight: '700px', overflow: 'auto' }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'email'}
                    direction={orderBy === 'email' ? order : 'asc'}
                    onClick={() => handleRequestSort('email')}
                  >
                    Email
                  </TableSortLabel>
                </TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Region</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedUsers.map((user) => {
                const canEditOrDelete = canManageRole(
                  currentUser ? currentUser.role_id : 3,
                  user.role_id
                );

                return (
                  <TableRow key={user.id}>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{getRoleLabel(user.role_id).toUpperCase()}</TableCell>
                    <TableCell>{user.region || 'No Region'}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        onClick={() => handleEditClick(user)}
                        disabled={!canEditOrDelete}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => handleDeleteClick(user)}
                        disabled={!canEditOrDelete}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={Boolean(deleteUserId)} onClose={handleCancelDelete}>
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete user {deleteUserEmail}?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="primary">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <AddUserModal
        open={openAddUserModal}
        onClose={() => setOpenAddUserModal(false)}
        onAddUser={handleAddUser}
        loading={addingUser}
        regions={regions}
      />

      {editingUser && (
        <EditUserModal
          open={openEditUserModal}
          onClose={() => {
            setOpenEditUserModal(false);
            setEditingUser(null);
          }}
          user={editingUser}
          onUpdateUser={handleUpdateUser}
          regions={regions}
        />
      )}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={alertSeverity}
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default UsersPage;
